import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import {
  getCountryCallingCode,
  useSnackbarContext,
} from "@inductiveautomation/ws-components";

import { handleResponse } from "../../../utilities/api_response";
import { Profile } from "../Profile.types";
import { useErrorCodes } from "../../../hooks/useErrorCodes";
import { usePutPhoneNumber1Mutation } from "../api/profile.api";

const buildPayload = (value: Profile["phoneNumber"]) =>
  value?.number
    ? {
        countryCode: getCountryCallingCode(value.countryCode ?? "US"),
        number: value.number,
        country: value.countryCode ?? "US",
      }
    : null;

export const useUpdatePhoneNumber = (
  form: UseFormReturn<{
    countryCode?: string;
    number?: string;
  }>
) => {
  const { t } = useTranslation("translation", { keyPrefix: "profile.form" });
  const { addSnackbar } = useSnackbarContext();
  const { lookup } = useErrorCodes();

  const [mutatePhoneNumber] = usePutPhoneNumber1Mutation();

  const updatePhoneNumber = async (value: Profile["phoneNumber"]) => {
    const body = buildPayload(value);

    const response = await mutatePhoneNumber({
      phoneNumberPayload: {
        phoneNumber: body ?? undefined,
      },
    });

    handleResponse(response, {
      onSuccess: () => {
        form.clearErrors("number");
        addSnackbar(t("success_message", { key: t("labels.phone_number") }));
      },
      onError: (message) => {
        form.setError("number", {
          type: "custom",
          message: message
            ? lookup(message)
            : t("error_message", {
                key: t("labels.phone_number").toLowerCase(),
              }),
        });
      },
      fallbackErrorMessage: t("error_message", {
        key: t("labels.phone_number").toLowerCase(),
      }),
    });
  };

  return { updatePhoneNumber };
};
